import { useState } from 'react';
import {
  X,
  MessageSquare,
  User,
  Cpu,
  Users,
  BookOpen,
  Shield,
  Database,
  Sparkles,
  HelpCircle,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface LexChatbotPRIsMEModalProps {
  isOpen: boolean;
  onClose: () => void;
  department: string;
  domain: string;
}

const tabs = [
  { id: 'presenter', label: 'Te présenter', icon: User },
  { id: 'fonctionnement', label: 'Fonctionnement', icon: Cpu },
  { id: 'gouvernance', label: 'Gouvernance', icon: Users },
];

export default function LexChatbotPRIsMEModal({
  isOpen,
  onClose,
  department,
  domain: _domain,
}: LexChatbotPRIsMEModalProps) {
  const [activeTab, setActiveTab] = useState('presenter');

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-[95vw] my-4 max-h-[95vh] flex flex-col overflow-hidden"
          >
            <div className="bg-linear-to-r from-indigo-800 via-purple-700 to-pink-600 text-white p-8 relative">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-white" />
              </button>
              <div className="flex items-start gap-4 pr-12">
                <div className="w-16 h-16 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <MessageSquare className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <div className="text-sm font-semibold text-white/80">ChatBot PRIsME</div>
                    <div className="flex items-center gap-1 px-2 py-0.5 bg-white/20 backdrop-blur-sm rounded-full">
                      <Sparkles className="w-3 h-3 text-yellow-300" />
                      <span className="text-xs font-semibold text-white">LEX</span>
                    </div>
                  </div>
                  <h2 className="text-3xl font-bold mb-2">LEX — ChatBot PRIsME</h2>
                  <p className="text-white/90 text-lg mb-2">{department}</p>
                </div>
              </div>
            </div>

            <div className="border-b border-gray-200 bg-gray-50">
              <div className="flex overflow-x-auto">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`
                        flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap
                        ${activeTab === tab.id
                          ? 'text-indigo-600 border-b-2 border-indigo-600 bg-white'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                        }
                      `}
                    >
                      <Icon className="w-4 h-4" />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  {activeTab === 'presenter' && <TePresenterTab />}
                  {activeTab === 'fonctionnement' && <FonctionnementTab />}
                  {activeTab === 'gouvernance' && <GouvernanceTab />}
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

function TePresenterTab() {
  return (
    <div className="space-y-6">
      <div className="bg-linear-to-r from-indigo-50 to-pink-50 p-6 rounded-xl border-l-4 border-indigo-500">
        <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <User className="w-6 h-6 text-indigo-600" />
          Te présenter — Qui es-tu ?
        </h3>
        <p className="text-gray-700 leading-relaxed mb-4">
          Je suis <strong>LEX</strong>, l’assistant conversationnel IA de PRIsME BZ. Je réponds à tes questions à partir de la base de connaissances PRIsME (méthodologie, gouvernance, processus, référentiels) pour t’aider à trouver l’info rapidement et à rester aligné sur les standards BZ.
        </p>
      </div>

      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
        <h4 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-indigo-600" />
          Qui t’a créé et pourquoi tu existes ?
        </h4>
        <p className="text-gray-700 leading-relaxed">
          L’équipe <strong>Programmation & IA</strong> de BZ (Dany, Jean-Michel et l’équipe technique) m’a conçu et m’alimente via <strong>PRIsME Master</strong>. J’existe pour que chaque employé BZ ait un accès rapide, fiable et contextualisé au savoir PRIsME — sans chercher dans des dizaines de documents — et pour renforcer la cohérence des réponses (méthodologie, processus, gouvernance).
        </p>
      </div>

      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
        <h4 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-indigo-600" />
          D’où vient ton nom ? (court, précis)
        </h4>
        <ul className="text-gray-700 leading-relaxed space-y-2">
          <li><strong>• LEX</strong> = <strong>REX</strong> — comme un chien qui écoute <em>au doigt et à l’œil</em> : réactif, fidèle, à l’écoute.</li>
          <li><strong>• LEX</strong> = <strong>Lexique</strong> — le lexique PRIsME : terminologie, concepts, processus en un seul endroit.</li>
          <li><strong>• LEX</strong> = assistant <strong>IA</strong> au service de PRIsME ; court, mémorisable, pro.</li>
        </ul>
      </div>

      <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-xl">
        <h4 className="text-xl font-bold text-amber-900 mb-2 flex items-center gap-2">
          <HelpCircle className="w-5 h-5 text-amber-600" />
          As-tu des questions ?
        </h4>
        <p className="text-amber-800 leading-relaxed">
          Oui. Pose-moi tes questions sur PRIsME, les processus, la gouvernance ou les référentiels. Si tu ne trouves pas la réponse, dis-le : le contenu est enrichi en continu par l’équipe (gestionnaires, Dany, Jean-Michel) et le contrôle qualité (Jean-Pierre, Pierre-Luc) pour que je réponde <strong>sur la coche</strong>.
        </p>
      </div>
    </div>
  );
}

function FonctionnementTab() {
  return (
    <div className="space-y-6">
      <div className="bg-linear-to-r from-purple-50 to-pink-50 p-6 rounded-xl border-l-4 border-purple-500">
        <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Cpu className="w-6 h-6 text-purple-600" />
          Fonctionnement dans son ensemble
        </h3>
        <p className="text-gray-700 leading-relaxed mb-4">
          <strong>PRIsME Master</strong> est le <strong>cœur</strong> du système. C’est lui qui contient toute la base de connaissances (fichiers knowledge) et qui alimente les assistants PRIsME. Même base de données, même source de vérité.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-5 rounded-xl border-2 border-indigo-200 shadow-sm">
          <div className="text-2xl font-bold text-indigo-600 mb-2">PRIsME Master</div>
          <p className="text-sm text-gray-700">Cœur du système. Base de connaissances complète. Alimente LEX (Employé) et, à venir, PRIsME Client.</p>
        </div>
        <div className="bg-white p-5 rounded-xl border-2 border-emerald-200 shadow-sm">
          <div className="text-2xl font-bold text-emerald-600 mb-2">PRIsME Employé</div>
          <p className="text-sm text-gray-700">Même DB que Master. LEX répond aux employés BZ à partir de tout le knowledge PRIsME (méthodologie, processus, référentiels).</p>
        </div>
        <div className="bg-white p-5 rounded-xl border-2 border-amber-200 shadow-sm">
          <div className="text-2xl font-bold text-amber-600 mb-2">PRIsME Client (à venir)</div>
          <p className="text-sm text-gray-700">Sera alimenté par Master une fois un <strong>cadre de sécurité</strong> en place pour ne pas divulguer tout notre knowledge aux clients.</p>
        </div>
      </div>

      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
        <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Database className="w-6 h-6 text-indigo-600" />
          Même base, plusieurs interfaces
        </h4>
        <p className="text-gray-700 leading-relaxed mb-4">
          PRIsME Master et PRIsME Employé utilisent la <strong>même base de connaissances</strong> (fichiers .md dans <code className="bg-gray-100 px-1 rounded">public/knowledge</code>, Vector Store OpenAI). Les gestionnaires créent et mettent à jour le contenu ; Dany et Jean-Michel programment et alimentent LEX via PRIsME Master. PRIsME Client, quand il sera déployé, aura un sous-ensemble contrôlé du knowledge pour protéger les informations internes BZ.
        </p>
      </div>

      <div className="bg-slate-50 border-l-4 border-slate-500 p-6 rounded-xl">
        <h4 className="text-xl font-bold text-slate-900 mb-2 flex items-center gap-2">
          <Shield className="w-5 h-5 text-slate-600" />
          PRIsME Client à venir
        </h4>
        <p className="text-slate-800 leading-relaxed">
          L’objectif est d’offrir un LEX (ou un assistant dérivé) aux clients, sans exposer l’ensemble du knowledge interne. Un <strong>cadre de sécurité</strong> (filtrage des fichiers, périmètre client, gouvernance) sera implémenté avant le déploiement pour éviter de divulguer tout notre savoir-faire et nos processus internes.
        </p>
      </div>
    </div>
  );
}

function GouvernanceTab() {
  return (
    <div className="space-y-6">
      <div className="bg-linear-to-r from-indigo-50 to-purple-50 p-6 rounded-xl border-l-4 border-indigo-500">
        <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Users className="w-6 h-6 text-indigo-600" />
          Gouvernance LEX — Qui fait quoi
        </h3>
        <p className="text-gray-700 leading-relaxed mb-4">
          Répartition claire des rôles pour que LEX reste à jour, pertinent et que ses réponses soient <strong>sur la coche</strong>.
        </p>
      </div>

      <div className="space-y-4">
        <div className="flex gap-4 bg-white p-5 rounded-xl border-2 border-indigo-100">
          <div className="w-12 h-12 rounded-full bg-indigo-500 text-white font-bold flex items-center justify-center shrink-0">1</div>
          <div>
            <h4 className="font-bold text-gray-900">Dany & Jean-Michel — Programmation et alimentation</h4>
            <p className="text-sm text-gray-700 mt-1">Ils <strong>programment</strong> et <strong>alimentent LEX</strong> par le biais de <strong>PRIsME Master</strong> : intégration des fichiers knowledge, configuration de l’assistant, mise à jour technique.</p>
          </div>
        </div>
        <div className="flex gap-4 bg-white p-5 rounded-xl border-2 border-emerald-100">
          <div className="w-12 h-12 rounded-full bg-emerald-500 text-white font-bold flex items-center justify-center shrink-0">2</div>
          <div>
            <h4 className="font-bold text-gray-900">Les gestionnaires — Création du contenu</h4>
            <p className="text-sm text-gray-700 mt-1">Ils <strong>créent le contenu</strong> (méthodologie, processus, référentiels, procédures) qui alimente la base de connaissances. Leur travail est intégré dans PRIsME Master puis utilisé par LEX.</p>
          </div>
        </div>
        <div className="flex gap-4 bg-white p-5 rounded-xl border-2 border-amber-100">
          <div className="w-12 h-12 rounded-full bg-amber-500 text-white font-bold flex items-center justify-center shrink-0">3</div>
          <div>
            <h4 className="font-bold text-gray-900">Jean-Pierre & Pierre-Luc — Contrôle qualité</h4>
            <p className="text-sm text-gray-700 mt-1">Ils s’assurent que <strong>LEX répond sur la coche</strong> : validation des réponses, cohérence avec les standards BZ, remontée des écarts pour ajuster le contenu ou la configuration.</p>
          </div>
        </div>
      </div>

      <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-xl">
        <h4 className="text-xl font-bold text-green-900 mb-2">Résumé gouvernance</h4>
        <p className="text-green-800 leading-relaxed">
          <strong>Dany / Jean-Michel</strong> : programme et alimente LEX via PRIsME Master. <strong>Gestionnaires</strong> : créent le contenu. <strong>Jean-Pierre / Pierre-Luc</strong> : contrôle qualité pour que LEX réponde sur la coche.
        </p>
      </div>
    </div>
  );
}
